import React, { useState, useEffect, useRef, memo } from 'react';
import Freundebuch, { EDM_SUBGENRES } from './Freundebuch';
import { LoadingAnimation3D } from '../common/LoadingAnimation3D';
import { completePKIFlow } from '../../services/pkiApi';
import audioPlayer from '../../services/audioPlayer';

// DUMMY MODE - Set to true to skip backend, false for real backend
const DUMMY_MODE = false;

export const PKIFlow = memo(function PKIFlow({ isActive, onComplete, onQuestionChange, onPageChange, isStreamPlaying, onPlayPauseClick, onJoinClick }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [shuffledMessages, setShuffledMessages] = useState([]);
  const [navState, setNavState] = useState({
    showBack: false,
    showNext: false,
    nextDisabled: true,
    isLastPage: false,
    isResetButton: false,
    currentPage: 0,
    onBack: () => {},
    onNext: () => {},
  });
  const hasWarmedUpAudio = useRef(false);

  // Notify parent about page changes
  useEffect(() => {
    if (onPageChange && navState.currentPage !== undefined) {
      onPageChange(navState.currentPage);
    }
  }, [navState.currentPage, onPageChange]);

  const allLoadingMessages = [
    "Kalibriere Gesichtsbreite...",
    "Lade schmale Frequenzen...",
    "Komprimiere Wangenknochen...",
    "Berechne optimale Nasenwinkel...",
    "Streame direkt ins Gesicht...",
    "Suche nach Chris' Kaffeetasse...",
    "Warte auf Lenas Approval...",
    "Verstecke alle Mikrofon-Kabel...",
    "Entferne Hintergrundhusten...",
    "Lade unbezahlte Praktikanten...",
    "Überzeuge Bass, lauter zu sein...",
    "Frage Algorithmus nach seinem Befinden...",
    "Sortiere Schallwellen alphabetisch...",
    "Lade Ausreden für Sendelücken...",
    "Ignoriere konstruktives Feedback...",
    "Lösche Roberts neueste Updates...",
    "Überzeuge Franz davon zurückzukommen...",
    "Schreibe Valentins Kündigung..."
  ];

  // Shuffle and pick 5 random messages when loading starts
  useEffect(() => {
    if (isLoading) {
      const shuffled = [...allLoadingMessages].sort(() => Math.random() - 0.5);
      setShuffledMessages(shuffled.slice(0, 5));
    }
  }, [isLoading]);

  // Get current message based on progress (0-20%, 20-40%, etc.)
  const getCurrentLoadingMessage = () => {
    if (shuffledMessages.length === 0) return allLoadingMessages[0];
    const index = Math.min(Math.floor(loadingProgress / 20), 4);
    return shuffledMessages[index];
  };

  // Update parent component when loading
  useEffect(() => {
    if (onQuestionChange && isLoading) {
      onQuestionChange('Erstelle deinen personalisierten Stream ...');
    }
  }, [isLoading, onQuestionChange]);

  // Convert Freundebuch data to backend format
  const convertToBackendFormat = (userData) => {
    // Map Freundebuch fields to backend expected format
    // For custom activity, send the custom text instead of 'custom'
    const activityValue = userData.activity === 'custom'
      ? (userData.customActivity || 'Individuell')
      : userData.activity;

    return {
      name: userData.name,
      mood: userData.mood,
      age: userData.age,
      city: userData.city,
      living: userData.living,
      job: userData.job,
      activity: activityValue,
      interests: [...userData.interests, ...userData.customInterests],
      genres: [
        ...userData.genres,
        ...(userData.genres.includes('edm')
          ? (userData.edmSubgenres.length > 0 ? userData.edmSubgenres : EDM_SUBGENRES.map(s => s.value))
          : []),
      ],
      color: userData.color,
      dayPlan: userData.dayHighlights.map(h => `${h.time}: ${h.text}`).join(', '),
      responseTimeSeconds: userData.responseTimeSeconds || 60,
    };
  };

  const handleFreundebuchComplete = async (userData) => {
    // Warm up audio on first user interaction (required for mobile autoplay)
    if (!hasWarmedUpAudio.current) {
      hasWarmedUpAudio.current = true;
      audioPlayer.warmUp();
    }

    const selections = convertToBackendFormat(userData);
    setIsLoading(true);

    if (DUMMY_MODE) {
      // DUMMY MODE: Just wait 2 seconds and start stream with test profileId
      const testProfileId = 'test_' + Math.random().toString(36).substring(2, 10);
      setTimeout(() => {
        setIsLoading(false);
        onComplete({
          selections,
          profileId: testProfileId
        });
      }, 2000);
    } else {
      // Real backend mode
      try {
        const profileData = await completePKIFlow(selections, (progress) => {
          setLoadingProgress(progress.completionPercentage);
        });

        setIsLoading(false);
        setLoadingProgress(0);

        onComplete({
          selections,
          profileId: profileData.profile_id
        });
      } catch (error) {
        console.error('PKIFlow error:', error);
        // On error, start stream anyway (fallback to test stream)
        setIsLoading(false);
        setLoadingProgress(0);

        onComplete({
          selections,
          profileId: null
        });
      }
    }
  };

  // Show loading animation with progress bar
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full px-8 sm:-mt-16">
        <LoadingAnimation3D isPKI={true} />
        <div className="w-full max-w-xs mt-6 sm:mt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span className="transition-opacity duration-300">{getCurrentLoadingMessage()}</span>
            <span>{Math.round(loadingProgress)}%</span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-pink-500 rounded-full transition-all duration-300"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Show Freundebuch
  return (
    <div className="w-full h-full flex justify-center items-center relative">
      {/* Back Button / Reset Button */}
      <button
        className={`absolute left-0 -bottom-2 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 z-20 w-12 h-12 rounded-full border-2 border-gray-200 bg-gray-100 text-gray-500 flex items-center justify-center transition-all hover:bg-gray-200 ${!navState.showBack ? 'invisible' : ''}`}
        onClick={navState.onBack}
        disabled={!navState.showBack}
        aria-label={navState.isResetButton ? "Abbrechen" : "Zurück"}
      >
        {navState.isResetButton ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        )}
      </button>

      <Freundebuch
        onComplete={handleFreundebuchComplete}
        onQuestionChange={onQuestionChange}
        onNavigationChange={setNavState}
        onJoinClick={onJoinClick}
      />

      {/* Next Button */}
      <button
        className={`absolute z-20 w-12 h-12 rounded-full border-2 border-transparent bg-pink-500 text-white flex items-center justify-center transition-all ${
          navState.isNamePage
            ? 'bottom-24 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0 sm:top-1/2 sm:-translate-y-1/2 sm:bottom-auto'
            : 'right-0 -bottom-2 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2'
        } ${!navState.showNext ? 'invisible' : ''} ${navState.nextDisabled ? 'bg-gray-200 opacity-50 cursor-not-allowed' : 'hover:opacity-85'}`}
        onClick={navState.onNext}
        disabled={navState.nextDisabled}
        aria-label={navState.isLastPage ? 'Fertig' : 'Weiter'}
      >
        {navState.isLastPage ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        )}
      </button>
    </div>
  );
});
